import React, { useCallback, useMemo } from "react";
import {
  Button,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { sportmap } from "../utilities/sportmap";
import { useHistory } from "react-router-dom";
import CompetitionHighlights from "./CompetitionHighlights";
const gridStyle = {
  padding: "8px",
};

const SOCIAL_PREFIXES = {
  Instagram: "https://www.instagram.com/",
  Facebook: "https://www.facebook.com/",
  YouTube: "https://www.youtube.com/",
  Twitter: "https://www.twitter.com/",
  LinkedIn: "https://www.linkedin.com/in/",
  TikTok: "https://www.tiktok.com/@",
};

const getUsername = (key, fullValue) => {
  const prefix = SOCIAL_PREFIXES[key];
  if (!prefix || !fullValue) return fullValue ?? "";
  return fullValue.startsWith(prefix)
    ? fullValue.slice(prefix.length)
    : fullValue;
};

const prefixAdornment = (text) => (
  <InputAdornment position="start">
    <span style={{ color: "grey", whiteSpace: "nowrap" }}>{text}</span>
  </InputAdornment>
);

const ProfileForm = (props) => {
  const {
    active,
    meta,
    published,
    bio,
    handleSelection,
    listHistory,
    name,
    previousCompHistory,
    setBio,
    setName,
    setShowPasswordModal,
    setSocial,
    social,
    sport,
    secondSport,
  } = props;
  const history = useHistory();
  const confirmProfile = useCallback(() => {
    // confirm profile will open a modal where you enter the password, then hit submit
    setShowPasswordModal(true);
  }, [setShowPasswordModal]);

  const filteredSports = useMemo(() => {
    return Object.keys(sportmap).filter((s) => s !== sport.toLowerCase());
  }, [sport]);

  return (
    <>
      <Grid item xs={8}>
        <Typography variant="h5" style={gridStyle}>
          Athlete Profile
        </Typography>
      </Grid>
      <Grid item xs={4} style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={() => history.push("/athlete-portal")}>Back</Button>
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          required
          label="Name"
          value={name ?? ""}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          select
          label="Sport"
          value={sport ?? ""}
          name="sport"
          required
          onChange={handleSelection}
          fullWidth
        >
          {Object.keys(sportmap).map((key) => (
            <MenuItem key={key} value={sportmap[key]}>
              {key}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          select
          label="Is this athlete active?"
          value={active ?? ""}
          required
          name="active"
          onChange={handleSelection}
          fullWidth
        >
          {["yes", "no"].map((key) => (
            <MenuItem key={key} value={key}>
              {key}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          select
          label="Second Sport"
          value={secondSport ?? ""}
          name="secondSport"
          onChange={handleSelection}
          fullWidth
        >
          {filteredSports.map((key) => (
            <MenuItem key={key} value={sportmap[key]}>
              {key}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          select
          label="Has Meta Image - Admin use only"
          value={meta ?? ""}
          required
          name="meta"
          onChange={handleSelection}
          fullWidth
        >
          {["yes", "no"].map((key) => (
            <MenuItem key={key} value={key}>
              {key}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          select
          label="Published - Admin use only"
          value={published ?? "yes"}
          required
          name="published"
          onChange={handleSelection}
          fullWidth
        >
          {["yes", "no"].map((key) => (
            <MenuItem key={key} value={key}>
              {key}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" style={gridStyle}>
          Bio
        </Typography>
      </Grid>
      <Grid item xs={3} style={gridStyle}>
        <TextField
          label="Birth Date"
          value={bio["Birth Year"] ?? ""}
          onChange={(e) => setBio({ ...bio, "Birth Year": e.target.value })}
          fullWidth
          helperText="enter by year '2025' or by date '01/01/2025'"
        />
      </Grid>
      <Grid item xs={3} style={gridStyle}>
        <TextField
          label="Vegan Since"
          value={bio["Vegan Since"] ?? ""}
          onChange={(e) => setBio({ ...bio, "Vegan Since": e.target.value })}
          fullWidth
          helperText="enter by year '2025' or by date '01/01/2025'"
        />
      </Grid>
      <Grid item xs={3} style={gridStyle}>
        <TextField
          label="Hometown"
          value={bio["Hometown"] ?? ""}
          onChange={(e) => setBio({ ...bio, Hometown: e.target.value })}
          fullWidth
        />
      </Grid>
      <Grid item xs={3} style={gridStyle}>
        <TextField
          label="Current City"
          value={bio["Current City"] ?? ""}
          onChange={(e) => setBio({ ...bio, "Current City": e.target.value })}
          fullWidth
        />
      </Grid>
      <Grid item xs={3} style={gridStyle}>
        <TextField
          label="Current Occupation"
          value={bio["Current Occupation"] ?? ""}
          onChange={(e) =>
            setBio({ ...bio, "Current Occupation": e.target.value })
          }
          fullWidth
        />
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          label="Division(s) you will compete in"
          value={bio["Division"] ?? ""}
          onChange={(e) =>
            setBio({
              ...bio,
              Division: e.target.value,
            })
          }
          fullWidth
        />
      </Grid>

      <Grid item xs={12} style={gridStyle}>
        <TextField
          label="Why you became a vegan"
          value={bio["Why you became a vegan"] ?? ""}
          onChange={(e) =>
            setBio({ ...bio, "Why you became a vegan": e.target.value })
          }
          fullWidth
          multiline
          minRows={1}
        />
      </Grid>
      <Grid item xs={12} style={gridStyle}>
        <TextField
          label="Favorite foods/meals"
          value={bio["Favorite foods/meals"] ?? ""}
          onChange={(e) =>
            setBio({ ...bio, "Favorite foods/meals": e.target.value })
          }
          fullWidth
          multiline
          minRows={1}
        />
      </Grid>
      <Grid item xs={12} style={gridStyle}>
        <TextField
          label="Favorite exercises"
          value={bio["Favorite exercises"] ?? ""}
          onChange={(e) =>
            setBio({ ...bio, "Favorite exercises": e.target.value })
          }
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">List Competition Highlights</Typography>
      </Grid>
      {listHistory && (
        <CompetitionHighlights
          records={bio["Competition Highlights"] ?? []}
          onChange={(newRecords) =>
            setBio({ ...bio, "Competition Highlights": newRecords })
          }
          previousCompHistory={previousCompHistory}
        />
      )}
      <Grid item xs={12}>
        <Typography variant="h6" style={gridStyle}>
          Social Media Links (optional)
        </Typography>
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          label="Website Link"
          value={social["Website"] ?? ""}
          onChange={(e) => setSocial({ ...social, Website: e.target.value })}
          fullWidth
          helperText="Use full url: example - https://www.plantbuilt.com"
        />
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          label="Instagram"
          value={getUsername("Instagram", social["Instagram"])}
          onChange={(e) =>
            setSocial({
              ...social,
              Instagram: SOCIAL_PREFIXES.Instagram + e.target.value,
            })
          }
          fullWidth
          InputProps={{
            startAdornment: prefixAdornment(SOCIAL_PREFIXES.Instagram),
          }}
          helperText="Enter your Instagram username"
        />
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          label="Facebook"
          value={getUsername("Facebook", social["Facebook"])}
          onChange={(e) =>
            setSocial({
              ...social,
              Facebook: SOCIAL_PREFIXES.Facebook + e.target.value,
            })
          }
          fullWidth
          InputProps={{
            startAdornment: prefixAdornment(SOCIAL_PREFIXES.Facebook),
          }}
          helperText="Enter your Facebook profile name"
        />
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          label="YouTube"
          value={getUsername("YouTube", social["YouTube"])}
          onChange={(e) =>
            setSocial({
              ...social,
              YouTube: SOCIAL_PREFIXES.YouTube + e.target.value,
            })
          }
          fullWidth
          InputProps={{
            startAdornment: prefixAdornment(SOCIAL_PREFIXES.YouTube),
          }}
          helperText="Enter your YouTube channel (e.g. @CoolVeganAthlete)"
        />
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          label="Twitter / X"
          value={getUsername("Twitter", social["Twitter"])}
          onChange={(e) =>
            setSocial({
              ...social,
              Twitter: SOCIAL_PREFIXES.Twitter + e.target.value,
            })
          }
          fullWidth
          InputProps={{
            startAdornment: prefixAdornment(SOCIAL_PREFIXES.Twitter),
          }}
          helperText="Enter your Twitter/X username"
        />
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          label="LinkedIn"
          value={getUsername("LinkedIn", social["LinkedIn"])}
          onChange={(e) =>
            setSocial({
              ...social,
              LinkedIn: SOCIAL_PREFIXES.LinkedIn + e.target.value,
            })
          }
          fullWidth
          InputProps={{
            startAdornment: prefixAdornment(SOCIAL_PREFIXES.LinkedIn),
          }}
          helperText="Enter your LinkedIn profile name"
        />
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          label="TikTok"
          value={getUsername("TikTok", social["TikTok"])}
          onChange={(e) =>
            setSocial({
              ...social,
              TikTok: SOCIAL_PREFIXES.TikTok + e.target.value,
            })
          }
          fullWidth
          InputProps={{
            startAdornment: prefixAdornment(SOCIAL_PREFIXES.TikTok),
          }}
          helperText="Enter your TikTok username (without @)"
        />
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          label="Podcast Link"
          value={social["Podcast"] ?? ""}
          onChange={(e) => setSocial({ ...social, Podcast: e.target.value })}
          fullWidth
          helperText="Use full url: example - https://podcasts.apple.com/us/podcast/vegancast"
        />
      </Grid>
      <Grid item xs={6} style={gridStyle}>
        <TextField
          label="Additional Link (optional)"
          value={social["AdditionalLink"] ?? ""}
          onChange={(e) =>
            setSocial({ ...social, AdditionalLink: e.target.value })
          }
          fullWidth
          helperText="Any other link — blog, Linktree, etc. Use full url."
        />
      </Grid>
      <Grid item xs={12} style={gridStyle}>
        <Button fullWidth onClick={confirmProfile}>
          Save and Submit
        </Button>
      </Grid>
    </>
  );
};

export default ProfileForm;
